import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { clientsApi, mattersApi, tasksApi, contactsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, Plus, Users, FolderKanban, CheckSquare, 
  Mail, Phone, Building2, Calendar, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const TURTLE_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/r0l0976o_Transparent%20BG%20Turtle.png';

const ClientDetail = () => {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [matterDialogOpen, setMatterDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  
  const [newContact, setNewContact] = useState({ name: '', email: '', phone: '', title: '', is_primary: false });
  const [newMatter, setNewMatter] = useState({ title: '', description: '', priority: 'medium', stage: 'planning', estimated_hours: 0 });
  const [newTask, setNewTask] = useState({ title: '', description: '', priority: 'medium', status: 'todo', estimated_hours: 0 });

  const fetchClient = async () => {
    try {
      const response = await clientsApi.getById(id);
      setClient(response.data);
    } catch (error) {
      toast.error('Failed to fetch client details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClient();
  }, [id]);

  const handleCreateContact = async (e) => {
    e.preventDefault();
    try {
      await contactsApi.create({ ...newContact, client_id: id });
      toast.success('Contact added successfully!');
      setContactDialogOpen(false);
      setNewContact({ name: '', email: '', phone: '', title: '', is_primary: false });
      fetchClient();
    } catch (error) {
      toast.error('Failed to add contact');
    }
  };

  const handleCreateMatter = async (e) => {
    e.preventDefault();
    try {
      await mattersApi.create({ ...newMatter, client_id: id });
      toast.success('Matter created successfully!');
      setMatterDialogOpen(false);
      setNewMatter({ title: '', description: '', priority: 'medium', stage: 'planning', estimated_hours: 0 });
      fetchClient();
    } catch (error) {
      toast.error('Failed to create matter');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await tasksApi.create({ ...newTask, client_id: id });
      toast.success('Task created successfully!');
      setTaskDialogOpen(false);
      setNewTask({ title: '', description: '', priority: 'medium', status: 'todo', estimated_hours: 0 });
      fetchClient();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'healthy': return 'bg-green-100 text-green-700';
      case 'attention': return 'bg-yellow-100 text-yellow-700';
      case 'critical': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'done': case 'completed': return 'bg-green-100 text-green-700';
      case 'in_progress': case 'active': return 'bg-blue-100 text-blue-700';
      case 'todo': case 'planning': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getAvatarColor = (name) => {
    const colors = ['#106EFD', '#40CFDE', '#37C563', '#FF801D', '#E171EA', '#FFD166'];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 loading-spinner"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="empty-state py-16">
        <img src={TURTLE_MASCOT} alt="Turtle mascot" className="w-32 h-32 mb-4" />
        <h3 className="text-xl font-['Amaranth'] text-[#1A2B5E] mb-2">Client not found</h3>
        <Link to="/clients">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Clients
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/clients">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div 
            className="w-14 h-14 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${getAvatarColor(client.name)}20` }}
          >
            <span 
              className="font-bold text-xl"
              style={{ color: getAvatarColor(client.name) }}
            >
              {client.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
            </span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">{client.name}</h1>
            <p className="text-navy/60">{client.industry || 'No industry specified'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={getHealthColor(client.health_status)}>
            {client.health_status}
          </Badge>
          <Badge className={getPriorityColor(client.priority)}>
            {client.priority} priority
          </Badge>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-white border-navy/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#106EFD]/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-[#106EFD]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">{client.contacts?.length || 0}</p>
              <p className="text-xs text-navy/60">Contacts</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-navy/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#40CFDE]/10 flex items-center justify-center">
              <FolderKanban className="w-5 h-5 text-[#40CFDE]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">{client.matters?.length || 0}</p>
              <p className="text-xs text-navy/60">Matters</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-navy/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#37C563]/10 flex items-center justify-center">
              <CheckSquare className="w-5 h-5 text-[#37C563]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">{client.tasks?.length || 0}</p>
              <p className="text-xs text-navy/60">Tasks</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-navy/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#FF801D]/10 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-[#FF801D]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">
                {client.tasks?.filter(t => t.status !== 'done').length || 0}
              </p>
              <p className="text-xs text-navy/60">Open Tasks</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="matters" className="space-y-4">
        <TabsList className="bg-white border border-navy/10">
          <TabsTrigger value="matters" data-testid="tab-matters">Matters</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
        </TabsList>

        {/* Matters Tab */}
        <TabsContent value="matters">
          <Card className="bg-white border-navy/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-['Amaranth']">Matters / Projects</CardTitle>
              <Dialog open={matterDialogOpen} onOpenChange={setMatterDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90" size="sm" data-testid="add-matter-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Matter
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-['Amaranth']">Add New Matter</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateMatter} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={newMatter.title}
                        onChange={(e) => setNewMatter({ ...newMatter, title: e.target.value })}
                        placeholder="Matter title"
                        required
                        data-testid="matter-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={newMatter.description}
                        onChange={(e) => setNewMatter({ ...newMatter, description: e.target.value })}
                        placeholder="Brief description"
                        data-testid="matter-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Priority</Label>
                        <Select value={newMatter.priority} onValueChange={(value) => setNewMatter({ ...newMatter, priority: value })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Stage</Label>
                        <Select value={newMatter.stage} onValueChange={(value) => setNewMatter({ ...newMatter, stage: value })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="planning">Planning</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="review">Review</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setMatterDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="create-matter-btn">Create Matter</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {client.matters?.length === 0 ? (
                <div className="empty-state py-8">
                  <img src={TURTLE_MASCOT} alt="Turtle" className="w-20 h-20 mb-3" />
                  <p className="text-navy/60">No matters yet. Create the first one!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {client.matters?.map((matter) => (
                    <Link key={matter.id} to={`/matters/${matter.id}`} className="block">
                      <div className="p-4 rounded-lg border border-navy/10 hover:border-navy/20 hover:shadow-sm transition-all">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-[#1A2B5E]">{matter.title}</h4>
                            {matter.description && <p className="text-sm text-navy/60 mt-1">{matter.description}</p>}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={getStatusColor(matter.stage)}>{matter.stage}</Badge>
                            <Badge className={getPriorityColor(matter.priority)}>{matter.priority}</Badge>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks">
          <Card className="bg-white border-navy/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-['Amaranth']">Tasks</CardTitle>
              <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90" size="sm" data-testid="add-task-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Task
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-['Amaranth']">Add New Task</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateTask} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={newTask.title}
                        onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                        placeholder="Task title"
                        required
                        data-testid="task-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={newTask.description}
                        onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                        placeholder="Brief description"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Priority</Label>
                        <Select value={newTask.priority} onValueChange={(value) => setNewTask({ ...newTask, priority: value })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Est. Hours</Label>
                        <Input
                          type="number"
                          value={newTask.estimated_hours}
                          onChange={(e) => setNewTask({ ...newTask, estimated_hours: parseFloat(e.target.value) || 0 })}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="create-task-btn">Create Task</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {client.tasks?.length === 0 ? (
                <div className="empty-state py-8">
                  <img src={TURTLE_MASCOT} alt="Turtle" className="w-20 h-20 mb-3" />
                  <p className="text-navy/60">No tasks yet. Create the first one!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {client.tasks?.map((task) => (
                    <div key={task.id} className="p-3 rounded-lg border border-navy/10 hover:border-navy/20 transition-all flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-[#1A2B5E]">{task.title}</h4>
                        {task.due_date && (
                          <p className="text-xs text-navy/50 flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            Due: {new Date(task.due_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(task.status)}>{task.status?.replace('_', ' ')}</Badge>
                        <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts">
          <Card className="bg-white border-navy/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-['Amaranth']">Contacts</CardTitle>
              <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90" size="sm" data-testid="add-contact-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Contact
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-['Amaranth']">Add New Contact</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateContact} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={newContact.name}
                        onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                        placeholder="Contact name"
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input
                        value={newContact.title}
                        onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
                        placeholder="Job title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newContact.email}
                        onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                        placeholder="email@example.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={newContact.phone}
                        onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setContactDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="create-contact-btn">Add Contact</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {client.contacts?.length === 0 ? (
                <div className="empty-state py-8">
                  <img src={TURTLE_MASCOT} alt="Turtle" className="w-20 h-20 mb-3" />
                  <p className="text-navy/60">No contacts yet. Add the first one!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {client.contacts?.map((contact) => (
                    <div key={contact.id} className="p-4 rounded-lg border border-navy/10">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                          style={{ backgroundColor: getAvatarColor(contact.name) }}
                        >
                          {contact.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div>
                          <h4 className="font-medium text-[#1A2B5E]">{contact.name}</h4>
                          {contact.title && <p className="text-sm text-navy/60">{contact.title}</p>}
                        </div>
                        {contact.is_primary && <Badge className="ml-auto bg-[#40CFDE]/20 text-[#1A2B5E]">Primary</Badge>}
                      </div>
                      <div className="mt-3 space-y-1">
                        {contact.email && (
                          <p className="text-sm text-navy/60 flex items-center gap-2">
                            <Mail className="w-4 h-4" /> {contact.email}
                          </p>
                        )}
                        {contact.phone && (
                          <p className="text-sm text-navy/60 flex items-center gap-2">
                            <Phone className="w-4 h-4" /> {contact.phone}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Details Tab */}
        <TabsContent value="details">
          <Card className="bg-white border-navy/10">
            <CardHeader>
              <CardTitle className="font-['Amaranth']">Client Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-navy/60">Company Name</Label>
                  <p className="font-medium text-[#1A2B5E] mt-1">{client.name}</p>
                </div>
                <div>
                  <Label className="text-navy/60">Industry</Label>
                  <p className="font-medium text-[#1A2B5E] mt-1">{client.industry || 'Not specified'}</p>
                </div>
                <div>
                  <Label className="text-navy/60">Status</Label>
                  <p className="font-medium text-[#1A2B5E] mt-1 capitalize">{client.status}</p>
                </div>
                <div>
                  <Label className="text-navy/60">Priority</Label>
                  <p className="font-medium text-[#1A2B5E] mt-1 capitalize">{client.priority}</p>
                </div>
                <div>
                  <Label className="text-navy/60">Health Status</Label>
                  <p className="font-medium text-[#1A2B5E] mt-1 capitalize">{client.health_status}</p>
                </div>
                <div>
                  <Label className="text-navy/60">Created</Label>
                  <p className="font-medium text-[#1A2B5E] mt-1">
                    {new Date(client.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              {client.description && (
                <div>
                  <Label className="text-navy/60">Description</Label>
                  <p className="font-medium text-[#1A2B5E] mt-1">{client.description}</p>
                </div>
              )}
              {client.tags?.length > 0 && (
                <div>
                  <Label className="text-navy/60">Tags</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {client.tags.map((tag, idx) => (
                      <Badge key={idx} variant="outline">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientDetail;
